import { forwardRef, useRef } from 'react';

import Times from '@decisiv/iconix/lib/components/Times';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';
import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import { DropdownListProps } from '@decisiv/ui-components/lib/components/DropdownList';
import Button from '@decisiv/ui-components/lib/components/Button';
import Loading from '@decisiv/ui-components/lib/components/Loading';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Tag from '@decisiv/ui-components/lib/components/Tag';

import { DropdownListItem } from '@decisiv/ui-components/lib/components/DropdownList/types';
import { BorderlessInput, TagContainer, TagInputContainer } from './styles';

type TargetProps = {
  label: string;
  name: string;
  disabled: boolean;
  selectedIds: string[];
  items: DropdownListProps['items'];
  isPopoverVisible: boolean;
  togglePopover: () => void;
  removeTag: (id: string | undefined) => void;
  searchText: string;
  onSearchTextChange: (text: string) => void;
  /** Shows a spinner in place of the chevron while the options are fetched. */
  loading?: boolean;
  /**
   * When set, an empty selection renders as this single, non-removable tag
   * instead of no tags at all — "no filter" shown as "everything".
   */
  allTagLabel?: string;
};

const Target = forwardRef<HTMLDivElement, TargetProps>(
  (
    {
      name,
      label,
      items,
      disabled,
      selectedIds,
      removeTag,
      togglePopover,
      isPopoverVisible,
      searchText,
      onSearchTextChange,
      allTagLabel,
      loading = false,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    /* istanbul ignore next */
    const icon = isPopoverVisible ? ChevronUp : ChevronDown;

    const tagItems = selectedIds
      ?.map((id) => {
        const foundOption = items?.find((option) => option.id === id);
        if (foundOption) {
          return foundOption;
        }
        return null;
      })
      .filter(Boolean) as DropdownListItem[];

    // Keyed off the value itself, not off `tagItems`. Those stay empty while
    // the options are still loading, and showing "ALL" then would claim the
    // opposite of a selection the user actually has.
    const showAllTag = !!allTagLabel && selectedIds.length === 0;

    const onTagClicked = (e: React.MouseEvent<HTMLDivElement>) => {
      /* istanbul ignore next -- @preserve */
      if (disabled) return;
      const target = e.target as HTMLElement;
      const tagged = target?.closest('[data-tag="selected-tag"]');
      if (tagged) {
        return;
      }
      if (target === inputRef.current && isPopoverVisible) {
        return;
      }
      togglePopover();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      /* istanbul ignore next -- @preserve */
      if (disabled) return;
      onSearchTextChange?.(e.target.value);
      if (!isPopoverVisible) {
        togglePopover();
      }
    };
    const containerId = `${name}-tag-container`;

    return (
      <TagContainer
        ref={ref}
        onClick={onTagClicked}
        disabled={disabled}
        aria-disabled={disabled}
      >
        <P as="label" size="small" shade={1} htmlFor={containerId}>
          {label}
        </P>
        <Flex flex={1} marginTop={0.5}>
          <TagInputContainer>
            {showAllTag && <Tag data-tag="all-tag" text={allTagLabel} />}
            {tagItems.map((item) => (
              <Tag
                data-tag="selected-tag"
                key={item?.id}
                text={item?.label}
                actionIcon={disabled ? undefined : Times}
                action={disabled ? undefined : () => removeTag(item.id)}
                actionAttributes={{
                  'aria-label': `Remove ${item?.label} filter`,
                }}
              />
            ))}
            <BorderlessInput
              id={containerId}
              type="text"
              ref={inputRef}
              name={name}
              value={searchText}
              disabled={disabled}
              onChange={handleInputChange}
            />
          </TagInputContainer>
          <Flex justifySelf="end" alignSelf="end" marginRight={1}>
            {loading ? (
              <Flex marginX={0.5} marginY={0.5} data-testid={`${name}-loading`}>
                <Loading size="small" />
              </Flex>
            ) : (
              <Button
                tabIndex={-1}
                icon={icon}
                variant="ghost"
                size="small"
                disabled={disabled}
              />
            )}
          </Flex>
        </Flex>
      </TagContainer>
    );
  },
);

export default Target;
